import { ModalProps } from 'antd';
import React from 'react';

/**
 * Source of modal close action
 */
export type CloseSource = 'ok' | 'cancel' | 'programmatic' | 'esc' | 'mask';

/**
 * Reason for modal close with source information
 */
export type CloseReason = { source: CloseSource };

/**
 * Props automatically injected into modal components
 */
export type ModalInjectedProps<TResult = void> = {
  /** Antd visible flag (mirrors Modal open state) */
  visible: boolean;
  /** Resolve the open() promise with a typed result and close (unless prevented) */
  resolve: (result?: TResult) => void;
  /** Reject the open() promise and close (unless prevented) */
  reject: (err?: unknown) => void;
  /** Programmatically close without resolving/rejecting */
  close: () => void;
  /** Imperative state helpers (for OK loading spinners, etc.) */
  setOkLoading: (loading: boolean) => void;
  setCancelLoading: (loading: boolean) => void;
  /** API object for accessing modal state and controls */
  api: ModalInstanceApi<TResult>;
};

/**
 * Contract for form modal components that handle typed form data
 */
export type ModalFormContract<TValues, TResult> = {
  /** Called when form is submitted with form values */
  onSubmit: (values: TValues, api: ModalInstanceApi<TResult>) => void | Promise<void>;
  /** Called when form is cancelled with optional partial values */
  onCancel: (
    ctx: { reason: CloseReason; values?: Partial<TValues> },
    api: ModalInstanceApi<TResult>
  ) => void | Promise<void>;
};

/**
 * API available to modal instance callbacks and interceptors
 */
export type ModalInstanceApi<TResult = void> = {
  /** Close controls */
  close: () => void;
  resolve: (result?: TResult) => void;
  reject: (err?: unknown) => void;
  /** Loading controls for default buttons */
  setOkLoading: (loading: boolean) => void;
  setCancelLoading: (loading: boolean) => void;
  /** Info about the instance */
  id: string;
};

/**
 * Configuration for footer buttons
 */
export type FooterButton<TResult = void> = {
  key: 'ok' | 'cancel' | string;
  label: React.ReactNode;
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** If true, provider will call resolve()/close() automatically after onClick unless prevented */
  autoClose?: boolean;
  onClick?: (api: ModalInstanceApi<TResult>) => void | Promise<void>;
};

/**
 * Options for opening a modal
 */
export type OpenOptions<TResult = void> = {
  /** Pass through to antd Modal */
  modalProps?: Partial<ModalProps>;

  /** Auto-close policy: true by default. Can be disabled globally or per action */
  autoClose?: boolean | { ok?: boolean; cancel?: boolean; esc?: boolean; mask?: boolean };

  /** Interceptors: return false to prevent close; return 'manual' to delegate to consumer */
  onBeforeOk?: (api: ModalInstanceApi<TResult>) => boolean | 'manual' | Promise<boolean | 'manual'>;
  onBeforeCancel?: (api: ModalInstanceApi<TResult>) => boolean | 'manual' | Promise<boolean | 'manual'>;

  /** Default footer is OK/Cancel. Override completely with a custom renderer */
  renderFooter?: (api: ModalInstanceApi<TResult>) => React.ReactNode;

  /** Or override via declarative, typed buttons (merged over defaults) */
  footerButtons?: FooterButton<TResult>[];
};

/**
 * Options for opening form modals with typed form data
 */
export type OpenFormOptions<TValues, TResult = TValues> = OpenOptions<TResult> & {
  /** Default form values or form initial state */
  defaultValues?: Partial<TValues>;

  /** Called when form is submitted - should handle the form data */
  onSubmit?: (values: TValues, api: ModalInstanceApi<TResult>) => void | Promise<void>;

  /** Called when form is cancelled */
  onCancel?: (
    ctx: { reason: CloseReason; values?: Partial<TValues> },
    api: ModalInstanceApi<TResult>
  ) => void | Promise<void>;
};

/**
 * Strictly typed open function
 */
export type OpenModalFn = <TProps extends Record<string, unknown>, TResult = void>(
  Component: React.ComponentType<TProps & ModalInjectedProps<TResult>>,
  props: TProps,
  options?: OpenOptions<TResult>
) => Promise<TResult | undefined>;

/**
 * Strictly typed open function for forms
 */
export type OpenFormModalFn = <TProps extends Record<string, unknown>, TValues, TResult = TValues>(
  Component: React.ComponentType<TProps & ModalFormContract<TValues, TResult> & ModalInjectedProps<TResult>>,
  props: TProps,
  options?: OpenFormOptions<TValues, TResult>
) => Promise<TResult | undefined>;

/**
 * Hook return type for useModal
 */
export interface UseModalReturn {
  open: OpenModalFn;
  openForm: OpenFormModalFn;
  confirm: (opts: import('antd').ModalFuncProps) => Promise<boolean>;
  closeAll: () => void;
}

/**
 * Internal modal instance state
 */
export interface ModalInstance<TResult = unknown> {
  id: string;
  Component: React.ComponentType<Record<string, unknown>>;
  props: Record<string, unknown>;
  visible: boolean;
  promise: {
    resolve: (result?: TResult) => void;
    reject: (err?: unknown) => void;
  };
  state: {
    okLoading: boolean;
    cancelLoading: boolean;
  };
  options: OpenOptions<TResult>;
  /** Form-specific options if this is a form modal */
  formOptions?: {
    defaultValues?: Record<string, unknown>;
    onSubmit?: (values: Record<string, unknown>, api: ModalInstanceApi<TResult>) => void | Promise<void>;
    onCancel?: (
      ctx: { reason: CloseReason; values?: Record<string, unknown> },
      api: ModalInstanceApi<TResult>
    ) => void | Promise<void>;
  };
}

/**
 * Modal provider context value
 */
export interface ModalContextValue {
  instances: ModalInstance[];
  open: OpenModalFn;
  openForm: OpenFormModalFn;
  confirm: (opts: import('antd').ModalFuncProps) => Promise<boolean>;
  closeAll: () => void;
  closeModal: (id: string) => void;
  resolveModal: <T>(id: string, result?: T) => void;
  rejectModal: (id: string, err?: unknown) => void;
  setModalLoading: (id: string, type: 'ok' | 'cancel', loading: boolean) => void;
}

/**
 * Modal reducer actions
 */
export type ModalAction =
  | { type: 'ADD_MODAL'; payload: ModalInstance }
  | { type: 'REMOVE_MODAL'; payload: string }
  | { type: 'SET_VISIBLE'; payload: { id: string; visible: boolean } }
  | { type: 'SET_LOADING'; payload: { id: string; type: 'ok' | 'cancel'; loading: boolean } }
  | { type: 'CLEAR_ALL' };

/**
 * Modal reducer state
 */
export interface ModalState {
  instances: ModalInstance[];
}

/**
 * Default auto-close configuration
 */
export const DEFAULT_AUTO_CLOSE = {
  ok: true,
  cancel: true,
  esc: true,
  mask: true,
} as const;

/**
 * Auto-close configuration type
 */
export type AutoCloseConfig = {
  ok: boolean;
  cancel: boolean;
  esc: boolean;
  mask: boolean;
};

/**
 * Normalize auto-close option to object form
 */
export function normalizeAutoClose(autoClose?: OpenOptions['autoClose']): AutoCloseConfig {
  if (typeof autoClose === 'boolean') {
    return {
      ok: autoClose,
      cancel: autoClose,
      esc: autoClose,
      mask: autoClose,
    };
  }
  return { ...DEFAULT_AUTO_CLOSE, ...autoClose };
}
